import React from "react";
import { Box, Typography, Chip, Button, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const LockIcon = ({ size = 15, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ReinvestHeader = ({
  upcomingQuarter = "Q3",
  previousQuarter = "Q2",
  isAdmin = false,
  onRestrictAccess,
  isRestricting = false,
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        gap: 2,
        mb: 3,
      }}
    >
      {/* Title + Subtitle */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.5rem", md: "1.75rem" },
              color: t.text,
              letterSpacing: "-0.02em",
            }}
          >
            Re-Invest for {upcomingQuarter}
          </Typography>
          <Chip
            label="Beta"
            size="small"
            sx={{
              backgroundColor: t.accent2,
              color: t.accent,
              border: `1px solid ${t.border2}`,
              fontWeight: 600,
              fontSize: "0.72rem",
              height: "22px",
              borderRadius: "12px",
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: t.text2,
            fontSize: "0.875rem",
          }}
        >
          Allocate your {upcomingQuarter} investments across other MACs and Groups using your available credit from {previousQuarter}.
        </Typography>
      </Box>

      {/* Access Status & Admin Actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        {/* Status indicator: Opened by Admin */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: isDark ? "rgba(16, 185, 129, 0.12)" : "rgba(45, 138, 91, 0.08)",
            border: `1px solid ${isDark ? "rgba(16, 185, 129, 0.35)" : "rgba(45, 138, 91, 0.25)"}`,
            borderRadius: "8px",
            px: 2,
            py: 0.9,
            boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.2)" : "0 1px 4px rgba(3, 81, 77, 0.08)",
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: isDark ? "#10B981" : "#2D8A5B",
              boxShadow: `0 0 8px ${isDark ? "#10B981" : "#2D8A5B"}`,
            }}
          />
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: isDark ? "#10B981" : "#2D8A5B",
              letterSpacing: "0.01em",
            }}
          >
            Opened by Admin
          </Typography>
        </Box>

        {/* Restrict Access button (INVESTMENT_LEAGUE_ADMIN_EMAILS only) */}
        {isAdmin && (
          <Button
            variant="outlined"
            onClick={onRestrictAccess}
            disabled={isRestricting}
            startIcon={
              isRestricting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <LockIcon size={15} color="currentColor" />
              )
            }
            sx={{
              borderColor: isDark ? "rgba(239, 68, 68, 0.4)" : "rgba(239, 68, 68, 0.35)",
              color: isDark ? "#FCA5A5" : "#DC2626",
              backgroundColor: isDark ? "rgba(239, 68, 68, 0.08)" : "rgba(239, 68, 68, 0.06)",
              fontWeight: 600,
              fontSize: "0.85rem",
              textTransform: "none",
              px: 2,
              py: 0.85,
              borderRadius: "8px",
              boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.2)" : "0 1px 4px rgba(239, 68, 68, 0.08)",
              "&:hover": {
                borderColor: "rgba(239, 68, 68, 0.7)",
                backgroundColor: isDark ? "rgba(239, 68, 68, 0.18)" : "rgba(239, 68, 68, 0.12)",
                color: isDark ? "#EF4444" : "#B91C1C",
              },
              "&.Mui-disabled": {
                borderColor: isDark ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.15)",
                color: isDark ? "rgba(252, 165, 165, 0.5)" : "rgba(220, 38, 38, 0.4)",
              },
            }}
          >
            {isRestricting ? "Restricting Access..." : "Restrict Access"}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ReinvestHeader;
